import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import ms from 'ms';

import { useQueryString } from '@hivebrite/react-hooks';

import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { dynamicTopicResourcePath } from 'hb-react/shared/apps/Topics/utils';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';

export const useQueryMemberships = (type, forceFiniteQuery = false) => {
  const { flagEvMembershipFoPagination: flagEvMembershipFoPaginationFF } =
    useFeatureFlag();
  const queryHook =
    !forceFiniteQuery && flagEvMembershipFoPaginationFF
      ? useInfiniteQueryMemberships
      : useFiniteQueryMemberships;

  return queryHook(type);
};

const useFiniteQueryMemberships = (type) => {
  const { query, indexPath, setFilters, setSearch, resetFilters } =
    useCommonSetup();
  const { filters, search } = query;

  const { data, isLoading } = useQuery({
    queryKey: ['finite', 'memberships', indexPath(), type, filters, search],
    queryFn: () =>
      axios
        .get(indexPath(), { params: { filters, type, search } })
        .then(({ data }) => data),
    staleTime: ms('5m'),
  });

  return {
    ...data,
    isLoading,
    resetFilters,
    setSearch,
    setFilters,
    filters,
  };
};

const useInfiniteQueryMemberships = (type) => {
  const { query, indexPath, setFilters, setSearch, resetFilters } =
    useCommonSetup();
  const { filters, search } = query;

  const fetchMemberships = ({ pageParam }) =>
    axios
      .get(indexPath(), {
        params: { filters, type, search, page: pageParam },
      })
      .then((res) => res.data);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['infinite', 'memberships', indexPath(), type, filters, search],
      queryFn: fetchMemberships,
      getNextPageParam: (lastPage) => lastPage.pagy[0].options.object?.next,
      staleTime: ms('5m'),
      initialPageParam: 1,
    });

  const allMemberships = data?.pages?.flatMap((page) => page.memberships) || [];

  return {
    memberships: allMemberships,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    resetFilters,
    setFilters,
    setSearch,
    indexPath: indexPath(),
    filters,
  };
};

const useCommonSetup = () => {
  const [query, setQuery] = useQueryString();
  const { currentUser } = useDefaultAppContexts();
  const topicContext = useTopicContext();

  const indexPath = () => {
    if (topicContext?.topicId) {
      const route = currentUser
        ? Routes.topics_topic_memberships_v2_index_path
        : Routes.topic_topics_memberships_v2_index_path;

      return dynamicTopicResourcePath(
        route(topicContext.topicId),
        topicContext,
      );
    }

    return currentUser
      ? Routes.users_memberships_v2_index_path()
      : Routes.memberships_v2_index_path();
  };

  const setSearch = (newSearch) => {
    setQuery({
      ...query,
      search: newSearch && newSearch.length > 0 ? newSearch : '',
    });
  };

  const setFilters = (selectedFilters) => {
    setQuery({ ...query, filters: selectedFilters });
  };

  const resetFilters = () => {
    setQuery({});
  };

  return { query, indexPath, setFilters, setSearch, resetFilters };
};
